import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editDefendantAppeal } from "services/appealService";
import { DefendantAppeal } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appeal?: DefendantAppeal;
  onClose: (refetch: boolean) => void;
};

const DefendantAppealResponseForm: React.FC<Props> = ({ appeal, onClose }) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<{
    response_summary: string;
    response_file: File | null;
  }>({
    response_summary: "",
    response_file: null,
  });

  const handleFile = (name: string, file: File | null) => {
    setFormState({ ...formState, [name]: file });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState?.response_summary) return t("response_summary_required");
    else if (!formState.response_file) return t("response_file_required");

    return "";
  };

  useEffect(() => {
    if (appeal) {
      setFormState((prev) => ({
        ...prev,
        response_summary: appeal?.response_summary ?? "",
      }));
    }
  }, [appeal]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    editDefendantAppeal({
      id: appeal?.id ?? "",
      response_summary: formState?.response_summary!,
      response_file_uploads: formState?.response_file!,
      coordinator_status: "PENDING",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((err) => {
        showTooltip(errorHandlerOnForm(err?.response?.data), "error");
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {appeal?.response_summary
                  ? t("edit_response", { defaultValue: "Edit Response" })
                  : t("prosecutor_response", {
                      defaultValue: "Prosecutor Response",
                    })}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("response_form_desc", {
                  defaultValue:
                    "Summarize the prosecutor's response to the appeal and attach the supporting document.",
                })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("response_summary")}
                <span className="text-red-500"> *</span>
              </label>
              <p className="text-xs text-gray-400 mb-2">
                {t("response_summary_hint", {
                  defaultValue:
                    "Describe the prosecutor's arguments and position on the appeal.",
                })}
              </p>
              <textarea
                className="bg-white border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring focus:ring-blue-300"
                name="response_summary"
                value={formState?.response_summary ?? ""}
                onChange={handleChange}
                rows={5}
              />
            </div>

            <div>
              <FileUpload
                label={t("response_file")}
                fileTypes="*"
                info={t("response_file_hint", {
                  defaultValue:
                    "Attach the signed response document (PDF or image).",
                })}
                required={true}
                onChange={(file) => handleFile("response_file", file)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("submit")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DefendantAppealResponseForm;
